import { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [servicesDropdown, setServicesDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setServicesDropdown(false);
      }
    };

    if (servicesDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [servicesDropdown]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [mobileMenuOpen]);

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/insights', label: 'Insights' },
    { path: '/about', label: 'Studio' },
    { path: '/contact', label: 'Contact' }
  ];

  const serviceItems = [
    { path: '/services/ai-image-generation', label: 'AI Image Generation' },
    { path: '/services/ai-video-generation', label: 'AI Video Generation' },
    { path: '/services/hybrid-ai-content', label: 'Hybrid AI Content' },
    { path: '/services/ugc-content', label: 'UGC Content' }
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-black/90 backdrop-blur-xl border-b border-white/10'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-12 py-3 lg:py-6 flex items-center justify-between">
        {/* Logo */}
        <Link to="/">
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="cursor-pointer"
          >
            <img
              src="https://static.readdy.ai/image/07328bf513a58d42d9d2e9cf0574d69f/9f9836521d58d58e1aa803a565d2a8be.png"
              alt="Black Velvet Studio"
              className="h-12 sm:h-20 lg:h-28 w-auto object-contain"
            />
          </motion.div>
        </Link>

        {/* Desktop Navigation Menu */}
        <div className="hidden lg:flex items-center gap-12">
          {navItems.map((item, index) => (
            <div key={item.path} className="flex items-center gap-12">
              <Link to={item.path}>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`text-sm font-medium tracking-wide transition-colors whitespace-nowrap cursor-pointer ${
                    location.pathname === item.path
                      ? 'text-brand-pink'
                      : 'text-white/60 hover:text-white'
                  }`}
                >
                  {item.label}
                </motion.button>
              </Link>
              {index === 0 && (
                <>
                  <span className="text-white/30">•</span>
                  {/* Services Dropdown */}
                  <div 
                    ref={dropdownRef}
                    className="relative"
                    onMouseEnter={() => setServicesDropdown(true)}
                    onMouseLeave={() => setServicesDropdown(false)}
                  >
                    <Link to="/services">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className={`text-sm font-medium tracking-wide transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                          location.pathname.startsWith('/services')
                            ? 'text-brand-pink'
                            : 'text-white/60 hover:text-white'
                        }`}
                      >
                        Services
                        <i className="ri-arrow-down-s-line text-xs"></i>
                      </motion.button>
                    </Link>
                    
                    <AnimatePresence>
                      {servicesDropdown && (
                        <motion.div
                          initial={{ opacity: 0, y: 10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 10, scale: 0.95 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full mt-4 left-0 bg-black/95 backdrop-blur-xl border border-white/10 rounded-2xl p-4 min-w-[220px] shadow-2xl"
                        >
                          {serviceItems.map((service) => (
                            <Link key={service.path} to={service.path}>
                              <motion.div
                                whileHover={{ x: 8, backgroundColor: 'rgba(255, 20, 147, 0.1)' }}
                                className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                                  location.pathname === service.path
                                    ? 'text-brand-pink bg-brand-pink/10'
                                    : 'text-white/70 hover:text-white'
                                }`}
                              >
                                {service.label}
                              </motion.div>
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </>
              )}
              {index < navItems.length - 1 && index !== 0 && (
                <span className="text-white/30">•</span>
              )}
            </div>
          ))}
        </div>

        {/* Desktop CTA Button */}
        <Link to="/contact" className="hidden lg:block">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="relative px-8 py-3 bg-gradient-to-r from-brand-pink to-brand-aqua rounded-full text-sm font-semibold overflow-hidden group whitespace-nowrap cursor-pointer text-white"
          >
            <span className="relative z-10">Start Creating</span>
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-brand-aqua to-brand-pink opacity-0 group-hover:opacity-100 transition-opacity"
            />
          </motion.button>
        </Link>

        {/* Mobile: CTA + Menu Button */}
        <div className="lg:hidden flex items-center gap-3">
          <Link to="/contact">
            <motion.button
              whileTap={{ scale: 0.95 }}
              className="px-5 py-2.5 bg-gradient-to-r from-brand-pink to-brand-aqua rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer text-white"
            >
              Start Creating
            </motion.button>
          </Link>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="w-10 h-10 flex items-center justify-center text-white cursor-pointer"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/10 overflow-hidden"
          >
            <div className="px-4 py-6 space-y-2">
              {navItems.map((item) => (
                <Link key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)}>
                  <div
                    className={`px-4 py-3 rounded-lg text-base font-medium cursor-pointer ${
                      location.pathname === item.path
                        ? 'text-brand-pink bg-brand-pink/10'
                        : 'text-white/70'
                    }`}
                  >
                    {item.label}
                  </div>
                </Link>
              ))}
              
              <div className="border-t border-white/10 pt-3 mt-3">
                <Link to="/services" onClick={() => setMobileMenuOpen(false)}>
                  <div className="px-4 py-3 rounded-lg text-base font-medium cursor-pointer text-white/70 flex items-center justify-between">
                    Services
                    <i className="ri-arrow-down-s-line text-lg"></i>
                  </div>
                </Link>
                <div className="pl-2 space-y-1 mt-1">
                  {serviceItems.map((service) => (
                    <Link key={service.path} to={service.path} onClick={() => setMobileMenuOpen(false)}>
                      <div
                        className={`px-4 py-2.5 rounded-lg text-sm font-medium cursor-pointer ${
                          location.pathname === service.path
                            ? 'text-brand-pink bg-brand-pink/10'
                            : 'text-white/60'
                        }`}
                      >
                        {service.label}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navigation;
